// components/common/UserSelect.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Select, Spin, SelectProps } from 'antd';
import { debounce } from 'lodash';
import { NubUserDTO } from '@/shared/api/model';
import { useSearchUserByField } from '@/shared/api/client/nub-user-resource';

interface UserOption {
    label: string;
    value: string;
}

// @ts-ignore
interface UserSelectProps extends Omit<SelectProps, 'options'> {
    value?: string;
    onChange?: (value: string, option: UserOption | UserOption[]) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    minSearchLength?: number;
    showEmail?: boolean;
}

export const UserSelect: React.FC<UserSelectProps> = ({
                                                          value,
                                                          onChange,
                                                          placeholder = 'Search users',
                                                          style,
                                                          disabled = false,
                                                          minSearchLength = 2,
                                                          showEmail = false,
                                                          ...selectProps
                                                      }) => {
    const [options, setOptions] = useState<UserOption[]>([]);
    const [searchText, setSearchText] = useState('');

    const {
        data: usersResponse,
        isLoading,
        isError,
        error
    } = useSearchUserByField("fullName", searchText);

    const formatUserLabel = useCallback((user: NubUserDTO): string => {
        const label = `${user.fullName} (${user.userId})`;
        return showEmail && user.email ? `${label} - ${user.email}` : label;
    }, [showEmail]);

    const updateOptions = useCallback((users: NubUserDTO[] | undefined) => {
        if (!users) {
            setOptions([]);
            return;
        }

        const newOptions = users.map(user => ({
            label: formatUserLabel(user),
            value: user.userId
        }));
// @ts-ignore
        setOptions(newOptions);
    }, [formatUserLabel]);

    useEffect(() => {
        if (usersResponse) {
            updateOptions(usersResponse);
        }
    }, [usersResponse, updateOptions]);

    const handleSearch = debounce((searchTerm: string) => {
        if (searchTerm.length < minSearchLength) {
            setOptions([]);
            return;
        }
        setSearchText(searchTerm);
    }, 300);

    const handleChange = (selectedValue: string, option: UserOption | UserOption[]) => {
        onChange?.(selectedValue, option);
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder={placeholder}
            style={style}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            filterOption={false}
            onSearch={handleSearch}
            // @ts-ignore
            onChange={handleChange}
            notFoundContent={
                isLoading ? <Spin size="small" /> :
                    isError ? `Error: ${(error as any)?.message || 'Failed to load users'}` :
                        searchText.length < minSearchLength ? `Type at least ${minSearchLength} characters to search` :
                            options.length === 0 ? 'No users found' : null
            }
            options={options}
            disabled={disabled}
            allowClear
            {...selectProps}
            status={isError ? 'error' : undefined}
        />
    );
};

// Add prop-types validation
UserSelect.defaultProps = {
    minSearchLength: 2,
    showEmail: false,
    placeholder: 'Search users',
    disabled: false
};